/**
 * Employee Explorer, with the effort/output scatter from §36.
 *
 * Revenue-per-activity is blank for anyone whose activity ids could not be tied
 * to their commission ids — the source uses four employee-id conventions with
 * no crosswalk, so the ratio is withheld rather than guessed.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type { EChartsOption } from "echarts";

import { api } from "@/api/client";
import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { useFilterStore } from "@/store/filters";
import type { AnalyticsEnvelope } from "@/types/api";
import { count, inr } from "@/utils/format";

interface EmployeeRow {
  person_key: string;
  name: string;
  designation: string;
  branch: string;
  activities: number;
  completed: number;
  clients: number;
  admissions: number;
  credits: number;
  revenue: number;
  commission: number;
  revenue_per_activity: number | null;
  identity_confident: boolean;
  needs_review: boolean;
}

interface Summary {
  employees: number;
  activities: number;
  revenue: number;
  identity_confident: number;
}

export function EmployeeList() {
  const navigate = useNavigate();
  const params = useFilterStore((s) => s.queryParams());

  // Server-side: the list is capped at 200, so a box that filters only what
  // came back reports "no such person" for anyone outside the cap.
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["employee-list", params, search],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<Summary>>("/employees", {
        params: { ...params, search: search || undefined },
      });
      return data;
    },
    staleTime: 300_000,
  });

  const rows = (data?.breakdown ?? []) as unknown as EmployeeRow[];
  const summary = data?.summary;
  const truncated = Boolean(
    summary?.employees && rows.length < summary.employees);

  // Effort against output. The interesting people are off the diagonal:
  // lots of activity with no admissions, or few activities with many.
  const scatter = useMemo<EChartsOption>(() => {
    const points = rows
      .filter((r) => r.activities > 0)
      .map((r) => ({
        value: [r.activities, r.admissions, Math.max(r.revenue, 0), r.name],
        name: r.name,
      }));
    const maxRevenue = Math.max(1, ...points.map((p) => Number(p.value[2])));
    return {
      tooltip: {
        trigger: "item",
        formatter: (p: unknown) => {
          const v = (p as { value: [number, number, number, string] }).value;
          return `<strong>${v[3]}</strong><br/>${v[0]} activities<br/>` +
                 `${v[1]} admissions<br/>${inr(v[2])} revenue`;
        },
      },
      xAxis: { type: "value", name: "Activities", nameLocation: "middle", nameGap: 28 },
      yAxis: { type: "value", name: "Admissions" },
      series: [{
        type: "scatter",
        data: points,
        symbolSize: (v: number[]) => 6 + (Number(v[2]) / maxRevenue) * 34,
        itemStyle: { color: "#0B5D57", opacity: 0.55 },
      }],
    };
  }, [rows]);

  const columns = useMemo<Column<EmployeeRow>[]>(() => [
    {
      key: "name", header: "Employee", width: "210px",
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          {r.name}
          {!r.identity_confident && (
            <AlertTriangle size={11} className="text-warn shrink-0"
              // The icon is the honest signal: this person's numbers are split
              // across identifier systems we could not join confidently.
            />
          )}
        </span>
      ),
      value: (r) => r.name,
    },
    { key: "branch", header: "Branch", width: "150px" },
    { key: "activities", header: "Activities", align: "right", value: (r) => r.activities },
    { key: "clients", header: "Clients", align: "right", value: (r) => r.clients },
    { key: "admissions", header: "Admissions", align: "right", value: (r) => r.admissions },
    {
      key: "revenue", header: "Revenue", align: "right",
      render: (r) => inr(r.revenue), value: (r) => r.revenue,
    },
    {
      key: "commission", header: "Commission", align: "right",
      render: (r) => inr(r.commission), value: (r) => r.commission,
    },
    {
      key: "revenue_per_activity", header: "Rev / activity", align: "right",
      render: (r) => (r.revenue_per_activity === null
        ? <span className="text-ink-faint" title="Identity not resolved confidently — withheld rather than guessed">—</span>
        : inr(r.revenue_per_activity)),
      value: (r) => r.revenue_per_activity,
    },
  ], []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">Employees</h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          Effort and output side by side. A blank ratio means that person's
          activity records and commission records are keyed in different
          identifier systems and could not be joined confidently.
        </p>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label="Employees active" value={count(summary?.employees)} />
        <KpiCard loading={isLoading} label="Activities" value={count(summary?.activities)} />
        <KpiCard loading={isLoading} label="Revenue attributed" value={inr(summary?.revenue)} />
        <KpiCard loading={isLoading} label="Identity resolved"
                 value={count(summary?.identity_confident)}
                 footnote="ratios are only shown for these" />
      </div>

      <div className="card p-4">
        <h2 className="font-display font-semibold text-[17px]">Effort against output</h2>
        <p className="text-[12.5px] text-ink-muted mb-3">
          Bubble size is revenue. The people worth a conversation are off the
          diagonal — high activity with no admissions, or few activities with many.
        </p>
        <Chart option={scatter} height={320} loading={isLoading} />
      </div>

      {truncated && (
        <p className="flex items-start gap-2 text-[12.5px] text-ink-muted">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warn" />
          <span>
            Showing {rows.length} of {count(summary?.employees)} people. The rest
            are not missing — the search box queries all of them.
          </span>
        </p>
      )}

      <DataTable<EmployeeRow>
        rows={rows}
        columns={columns}
        rowKey={(r) => r.person_key}
        onRowClick={(r) => navigate(`/employees/${encodeURIComponent(r.person_key)}`)}
        exportName="employees"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search every person by name or branch"
        loading={isLoading}
        emptyMessage="No employee activity in this period."
        footnote="Click a row for Employee 360. ⚠ marks an unresolved identity — see Administration › Identity links."
      />

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
    </div>
  );
}
